import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";

import AutoptoTrialTerms from "~/components/autopto/cardDialog/AutoptoTrialTerms";
import { isAutoptoCardRequired } from "~/components/autopto/isAutoptoTrial";
import { AlertCard } from "~/components/ui/card/AlertCard";
import Modal from "~/components/ui/modal/Modal";
import Spinner from "~/components/ui/spinner/Spinner";
import { useTeam } from "~/contexts/TeamProvider";
import { stripePromise } from "~/helpers/load_stripe";

type Props = {
  isOpen: boolean;
  clientSecret: string | null;
  isCreatingSession: boolean;
  onClose: () => void;
};

/**
 * Stripe.js fails to load behind some ad blockers and on a bad network: the
 * form would stay blank.
 */
const useIsStripeUnavailable = (isOpen: boolean) => {
  const [isUnavailable, setIsUnavailable] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let isActive = true;

    stripePromise.then(
      (stripe) => isActive && setIsUnavailable(!stripe),
      () => isActive && setIsUnavailable(true),
    );

    return () => {
      isActive = false;
    };
  }, [isOpen]);

  return isUnavailable;
};

/**
 * Stripe's embedded form of a setup session, which saves a card. During an
 * Autopto trial without a card our terms come above it.
 */
const AddCardModal = ({
  isOpen,
  clientSecret,
  isCreatingSession,
  onClose,
}: Props) => {
  const { team } = useTeam();
  const isStripeUnavailable = useIsStripeUnavailable(isOpen);

  return (
    <Modal
      key="setPaymentMethod"
      isOpen={isOpen}
      closeModal={onClose}
      formId="setPaymentMethod"
      height="92%"
      width="600px"
      // Stripe's form and our terms carry their own headings.
      modalLabel={<span />}
    >
      {isAutoptoCardRequired(team) && <AutoptoTrialTerms />}
      {isCreatingSession && <Spinner />}
      {isStripeUnavailable && (
        <AlertCard
          type="danger"
          title="The card form couldn't load"
          content="Check your connection, or pause your ad blocker for this site, then try again."
        />
      )}
      {clientSecret && !isStripeUnavailable && (
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}
    </Modal>
  );
};

export default AddCardModal;
